import React, { useEffect, useState } from "react";

import { Grid, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";

import Input from "../../../../../../../../components/Form/Input";
import InputRange from "../../../../../../../../components/Form/InputRange";
import RadioGroup from "../../../../../../../../components/Form/RadioGroup";
import Modal from "../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../context/StateContext";
import { ItemTypes } from "../../../../../../../../models/item.types";
import { Status } from "../../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import useStyles from "./styles";
import SelectMultiple from "Components/Form/SelectMultiple";
import { Branch } from "Models/branch";
import BranchService from "Services/branch.service";
import { AutoSuggest } from "Components/Form";
import { Room } from "Models/room";
import RoomService from "Services/room.service";

interface Props {
  itemTypeToUpdate?: ItemTypes;
  open: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  updateItemType?: (itemType: ItemTypes) => void;
  createItemType?: (itemType: ItemTypes) => void;
}

const ItemTypeModal = ({
  itemTypeToUpdate,
  open,
  isLoading,
  hasError,
  closeModal,
  updateItemType,
  createItemType,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const measurement = getMeasurement();

  const [itemTypeName, setItemTypeName] = useState<string>("");
  const [itemTypeCode, setItemTypeCode] = useState<string>("");
  const [itemTypeVolume, setItemTypeVolume] = useState<string>("0");
  const [itemTypeIsBox, setItemTypeIsBox] = useState<boolean>(false);
  const [itemTypeLocation, setItemTypeLocation] = useState<Room[]>([]);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  const [locationList, setLocationList] = useState<Room[]>([]);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    itemTypeName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (itemTypeName) =>
        itemTypeName ? itemTypeName !== "" : false
      ),
    itemTypeCode: yup.string(),
    itemTypeVolume: yup
      .string()
      .test("empty-check", "*This field must not be empty", (itemTypeVolume) =>
        itemTypeVolume ? itemTypeVolume.length > 0 : false
      ),
    itemTypeIsBox: yup.boolean(),
    branches: yup
      .array()
      .of(yup.mixed())
      .min(1, translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
  });

  const getBranchesId = () => {
    const branches = currentUser?.branches
      ?.filter((branch) => branch.companyId === currentUser.companyId)
      ?.map((branch) => branch?.id);

    if (!branches?.length) return [];

    return branches;
  };

  const { isFetching: isFetchingBranchesList } = useQuery(
    ["branch.list", currentUser?.companyId],
    () =>
      BranchService.getAll({
        id: getBranchesId(),
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const list: Branch[] = response.data;

          list.sort((a, b) => {
            if (a?.name && b?.name) {
              const aName = a.name.toLowerCase();
              const bName = b.name.toLowerCase();

              if (aName > bName) return 1;
              if (aName < bName) return -1;
            }

            return 0;
          });

          setBranchesList(list);
        }
      },
    }
  );

  const { isFetching: isFetchingRooms } = useQuery({
    queryKey: ["room.list"],
    queryFn: async () => {
      const response = await RoomService.getAll({
        status: Status.ACTIVE,
        jobId: "",
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        attributes: ["id", "name", "code"]
      });

      if (response && response.data && response.data.length > 0) {
        setLocationList(response.data);

        return response.data;
      }

      return [];
    },
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
  });

  const clearFields = () => {
    setItemTypeName("");
    setItemTypeCode("");
    setItemTypeVolume("0");
    setItemTypeIsBox(false);
    setBranches([]);
    setItemTypeLocation([])
  };

  const isValid = (itemType: any) => {
    try {
      schema.validateSync(
        {
          itemTypeName: itemType.name,
          itemTypeCode: itemType.code,
          itemTypeVolume: itemType.volume,
          itemTypeIsBox: itemType.isBox,
          branches: itemType.branches,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (itemTypeToUpdate) {
      if (updateItemType) {
        const object = {
          ...itemTypeToUpdate,
          name: itemTypeName,
          code: itemTypeCode,
          volume: itemTypeVolume,
          isBox: itemTypeIsBox,
          rooms: itemTypeLocation,
          status,
          branchIds: branches.map((b) => Number(b.id)),
          branches,
        };

        if (isValid(object)) updateItemType({ ...object, volume: Number(object.volume) });
      }
    } else if (createItemType) {
      const object = {
        name: itemTypeName,
        code: itemTypeCode,
        volume: itemTypeVolume,
        status,
        companyId: currentUser?.companyId,
        isBox: itemTypeIsBox,
        rooms: itemTypeLocation,
        branchIds: branches.map((b) => Number(b.id)),
        branches,
      };

      if (isValid(object)) createItemType({ ...object, volume: Number(object.volume) });
    }
  };

  const onUpdateVolumeItem = (value: number) => {
    if (measurement?.code === "ft3") setItemTypeVolume(`${Number(value) / 35.315}`);
    else setItemTypeVolume(`${value}`);
  };

  useEffect(() => {
    if (itemTypeToUpdate) {
      setItemTypeName(itemTypeToUpdate.name);
      setItemTypeCode(itemTypeToUpdate?.code || "");
      setItemTypeVolume(String(itemTypeToUpdate.volume));
      setStatus(itemTypeToUpdate?.status);
      setItemTypeIsBox(itemTypeToUpdate?.isBox);
      setItemTypeLocation(itemTypeToUpdate?.rooms || ([] as Room[]));

      if ((itemTypeToUpdate as any).branches) {
        setBranches((itemTypeToUpdate as any).branches as Branch[]);
      } else {
        setBranches([]);
      }
    } else {
      setBranches([]);
    }
  }, [itemTypeToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid({
        name: itemTypeName,
        code: itemTypeCode,
        volume: itemTypeVolume,
        isBox: itemTypeIsBox,
        branches,
      });
    }
  }, [itemTypeName, itemTypeCode, itemTypeVolume, hasFormBeenSubmitted, itemTypeIsBox, branches]);

  const renderSelectMultipleBranches = () => {
    return (
      <SelectMultiple
        loading={isFetchingBranchesList}
        required
        label={translate("branch")}
        dataCy="selectBranches"
        disabled={isLoading || isFetchingBranchesList}
        error={!!(getErrorByField("branches") && getErrorByField("branches") !== "")}
        errorMessage={getErrorByField("branches")}
        name="branchesList"
        onChange={(event: SelectChangeEvent<any>) => {
          if (event.target.value.includes(0) || event.target.value.includes(-1)) return;

          const branchesSelecteds = branchesList?.filter((branch) =>
            event.target.value.includes(branch.id)
          ) as Branch[];

          setBranches(branchesSelecteds);
        }}
        value={branches.map((branch) => Number(branch?.id)) || []}
        onAddAll={() => {
          setBranches(branchesList);
        }}
        onRemoveAll={() => {
          setBranches([]);
        }}
        renderInternalCount={false}
        items={branchesList.map((branch) => ({
          key: Number(branch.id),
          label: branch.name,
        }))}
        selecteds={
          branches?.map((branch) => ({
            key: Number(branch.id),
            label: branch.name,
          })) || []
        }
        style={{ marginTop: 10 }}
      />
    );
  };

  return (
    <Modal
      open={open}
      handleClose={closeModal}
      data-cy="itemTypeModal"
      title={itemTypeToUpdate ? translate("editPackagingType") : translate("createPackagingType")}
      dataCyTitle="itemTypeModalTitle"
      showActionButtons
      actionButtonProps={{
        onCancel: () => {
          clearFields();
          closeModal();
        },
        dataCyCancel: "cancelButton",
        textSave: itemTypeToUpdate ? translate("update") : translate("create"),
        dataCySave: "submitChangesButton",
        loading: isLoading,
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField
      statusFieldProps={{
        label: translate("packagingTypeStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
      showBranchesField
      renderBranchesField={renderSelectMultipleBranches}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="itemTypeCode"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("itemTypeCode")}
              errorMessage={getErrorByField("itemTypeCode")}
              value={itemTypeCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemTypeCode(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <RadioGroup
              data-cy="packagingTypeIsBox"
              label={translate("isBoxInterrogation")}
              value={itemTypeIsBox}
              onChange={(event) =>
                event?.target?.value === "true" ? setItemTypeIsBox(true) : setItemTypeIsBox(false)
              }
              options={[
                {
                  label: translate("yes"),
                  classes: classes.isBoxLabel,
                  value: true,
                  "data-cy": "packagingTypeIsBoxYes",
                },
                {
                  label: translate("no"),
                  classes: classes.isBoxLabel,
                  value: false,
                  "data-cy": "packagingTypeIsBoxNo",
                },
              ]}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              required
              disabled={isLoading}
              name="itemTypeName"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("itemTypeName")}
              errorMessage={getErrorByField("itemTypeName")}
              value={itemTypeName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemTypeName(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <InputRange
              disabled={isLoading}
              containerClassName={classes.containerInputRange}
              label={translate("volume")}
              name="itemTypeVolume"
              measurementInputRange={measurement}
              value={itemTypeVolume}
              onIncrease={(value) => onUpdateVolumeItem(value)}
              onDecrease={(value) => onUpdateVolumeItem(value)}
              change={(value) => {
                const nValue = Number(value);

                if (!Number.isNaN(nValue)) onUpdateVolumeItem(nValue);
              }}
              minValue={0}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <AutoSuggest
              disableCloseOnSelect
              disabled={isFetchingRooms}
              data-cy="autosuggestLocation"
              label={translate("location")}
              options={locationList}
              value={itemTypeLocation}
              inputValue={""}
              getOptionLabel={(location: Room) => (location && location.name ? location.name : "")}
              getOptionSelected={(option: Room, value: Room) => option.id === value.id}
              multiple
              onChange={(selectedRooms: Room[]) => {
                setItemTypeLocation(selectedRooms);
              }}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default ItemTypeModal;
